import type { ChatStatusData } from './chat-status';
import type { ConnectorStatus } from './connectors';
import type { WebRoutine } from './routines';

/**
 * Which parts of the chat Status panel are NEW, so they can come in one by one (`ChatStatusPanel`).
 *
 * The panel records every key it shows the first time its data settles for a conversation, EVEN WHEN
 * THAT IS NOTHING. That is what makes a column that appears mid-chat (the onboarding turn that creates a
 * whole band at once) animate: its cards were not there at the start, so every one of them arrives.
 * Opening a chat that already has them, or the overlay on top of one, animates nothing.
 *
 * Status keys carry the state they show, so an app getting connected, a routine switched, or a task
 * moving on counts as an arrival. Import-free (types only), so it is testable without the app's graph.
 */
export const ARRIVAL_STAGGER_MS = 140;

export const connectorStateKey = (connector: ConnectorStatus) => `connectorState:${connector.toolkit}:${connector.connected ? 'on' : 'off'}`;
export const routineKey = (routine: WebRoutine) => `routine:${routine.id}:${routine.paused ? 'off' : 'on'}`;

export function arrivalKeys(data: ChatStatusData | null, hasFiles: boolean): string[] {
  const keys: string[] = [];
  if (data) {
    if (data.spaces.length > 0) keys.push('card:space', ...data.spaces.map((space) => `space:${space.id}`));
    if (data.pendingBands.length > 0) keys.push('card:pendingTeam', ...data.pendingBands.map(pendingBandKey));
    if (data.agents.length > 0) keys.push('card:team', ...data.agents.map((agent) => `agent:${agent.id}`));
    if (data.connectors.length > 0) {
      keys.push('card:connectors');
      for (const connector of data.connectors) keys.push(`connector:${connector.toolkit}`, connectorStateKey(connector));
    }
    if (data.routines.length > 0) keys.push('card:routines', ...data.routines.map(routineKey));
    if (data.goals.length > 0) {
      keys.push('card:goal');
      for (const goal of data.goals) {
        keys.push(`goal:${goal.id}`);
        for (const task of goal.tasks) keys.push(`task:${task.id}`, `taskState:${task.id}:${task.status}`);
      }
    }
    if (data.tasks.length > 0) {
      keys.push('card:tasks');
      for (const task of data.tasks) keys.push(chatTaskKey(task.id), chatTaskStateKey(task.id, task.status));
    }
    if (data.knowledge.length > 0) keys.push('card:knowledge', ...data.knowledge.map((entry) => `knowledge:${entry.path}`));
  }
  if (hasFiles) keys.push('card:files');
  return keys;
}

/**
 * What the agents island shows, as one key per section and per row, for its own arrivals (Sarah Hindman's
 * island prototype): when the chat's status gains something new, the island drops its panel open by itself,
 * the new rows slide in one after another, and the panel closes again. Entities only, never states: a task
 * moving on or an app getting connected changes a row the reader already saw, and must not reopen the panel.
 */
export function islandArrivalKeys(data: ChatStatusData | null): string[] {
  if (!data) return [];
  const keys: string[] = [];
  const kind = (card: string, items: string[]) => {
    if (items.length > 0) keys.push(`card:${card}`, ...items);
  };
  kind('goal', data.goals.flatMap((goal) => [`goal:${goal.id}`, ...goal.tasks.map((task) => `task:${task.id}`)]));
  kind('tasks', data.tasks.map((task) => `task:${task.id}`));
  kind('team', data.agents.map((agent) => `agent:${agent.id}`));
  kind('connectors', data.connectors.map((connector) => `connector:${connector.toolkit}`));
  kind('routines', data.routines.map((routine) => `routine:${routine.id}`));
  kind('files', data.files.map((file) => `file:${file.vfsPath}`));
  kind('knowledge', data.knowledge.map((entry) => `knowledge:${entry.path}`));
  return keys;
}

/** The island keys not seen before, in the order the panel draws them. */
export function freshIslandKeys(known: ReadonlySet<string>, current: string[]): string[] {
  return current.filter((key) => !known.has(key));
}

/** One step of the island's one-after-another slide-in. */
export const ISLAND_ARRIVAL_STAGGER_MS = 250;
/** How long the island stays open after its last new row has slid in. */
export const ISLAND_PEEK_HOLD_MS = 2400;

/** How long a peek stays open for `freshCount` new keys: every slide-in, then the hold. */
export const islandPeekMs = (freshCount: number) => freshCount * ISLAND_ARRIVAL_STAGGER_MS + ISLAND_PEEK_HOLD_MS;

export const pendingBandKey =(band: { name: string }) => `pendingBand:${band.name}`;

/** A task the chat started outside its goal. Its own prefix, so it can never share a key with a goal's task. */
export const chatTaskKey = (taskId: string) => `chatTask:${taskId}`;
export const chatTaskStateKey = (taskId: string, status: string) => `chatTaskState:${taskId}:${status}`;

/** How long one arrival takes, from its delay to fully in place. */
export const ARRIVAL_ENTER_MS = 420;

/** Whether `key` arrived after the panel settled. Never before it settles (`initial` null). */
export function hasArrived(initial: ReadonlySet<string> | null, key: string): boolean {
  return initial !== null && !initial.has(key);
}

/**
 * How one key arrives. Fixed the first time the key is seen, so a re-render never moves or restarts it.
 *
 * `within`: it came in while its parent was itself still coming in. The parent already made the room, so it
 * only fades in. Otherwise it is entering a card or a list the reader already sees, and it has to GROW into
 * place: popping in at full height pushes every row under it down in one frame.
 */
export interface ArrivalEntry {
  delay: number;
  within: boolean;
  /** When this arrival is over, on the clock `ArrivalBatch.now` reads. */
  until: number;
}

export interface ArrivalTracker {
  conversationId: string | null;
  initial: Set<string> | null;
  /** `null`: the key is new but changes nothing on its own (a state badge on a row that is arriving). */
  entries: Map<string, ArrivalEntry | null>;
}

export const newArrivalTracker = (conversationId: string | null): ArrivalTracker => ({ conversationId, initial: null, entries: new Map() });

/**
 * One render's new keys. They take consecutive delays in the order the panel draws them, so a band's cards,
 * and the rows inside each card, come in one after another. `base` holds the whole batch back, for the
 * column that is still opening.
 */
export interface ArrivalBatch {
  next: number;
  base: number;
  now: number;
}

/**
 * How `key` arrives, or null when it does not move. A `swap` (a status badge that changed) never moves while
 * its own row is arriving: it is part of that row.
 */
export function arrivalOf(
  tracker: ArrivalTracker,
  key: string,
  batch: ArrivalBatch,
  opts: { parent?: ArrivalEntry | null; swap?: boolean } = {},
): ArrivalEntry | null {
  if (!hasArrived(tracker.initial, key)) return null;
  if (tracker.entries.has(key)) return tracker.entries.get(key) ?? null;
  const within = Boolean(opts.parent && opts.parent.until > batch.now);
  if (within && opts.swap) {
    tracker.entries.set(key, null);
    return null;
  }
  const delay = batch.base + batch.next * ARRIVAL_STAGGER_MS;
  batch.next += 1;
  const entry = { delay, within, until: batch.now + delay + ARRIVAL_ENTER_MS };
  tracker.entries.set(key, entry);
  return entry;
}
